import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SumsubWebSdk from '@sumsub/websdk-react';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';

import { AuthApi } from 'api/AuthApi';
import { SettingsApi } from 'api/SettingsApi';
import { TrackingApi } from 'api/TrackingApi';
import { updateFeatureFlag } from 'store/common/commonSlice';
import {
  getCurrentLangSelector,
  isNeedAgeVerificationEnabledSelector,
} from 'store/common/selectors';

import { Popup } from 'components/UI/Popup';

type ActionsPayload = BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

const options = {};

export const AgeVerificationPopup: React.FC<Props> = ({ onAction }) => {
  const [accessToken, setAccessToken] = useState<string>();

  const dispatch = useDispatch();

  const currentLang = useSelector(getCurrentLangSelector);
  const isNeedAgeVerificationEnabled = useSelector(
    isNeedAgeVerificationEnabledSelector
  );

  const config = useMemo(() => {
    return {
      lang: currentLang,
    };
  }, [currentLang]);

  const getAccessToken = async () => {
    try {
      const { token } = await AuthApi.getAgeVerificationToken(
        MirrorService.sumsubToken
      );
      setAccessToken(token);
    } catch (error) {
      TrackingApi.trackError({
        type: 'warning',
        message: `getAgeVerificationToken crash, error: ${error}`,
      });
    }
  };

  const handleClose = () => {
    onAction({ type: 'close' });
  };

  const handleExpiration = () => {
    setAccessToken('');
    getAccessToken();
  };

  const handleError = (error: any) => {
    TrackingApi.trackError({
      type: 'warning',
      message: `SumsubWebSdk crash, error: ${error?.error}, code: ${error?.code}`,
    });
  };

  const handleResult = async (type: any, payload: any) => {
    if (
      type === 'idCheck.onApplicantStatusChanged' &&
      payload?.reviewResult?.reviewAnswer === 'GREEN'
    ) {
      try {
        await SettingsApi.confirmAge();
      } catch (error) {
        TrackingApi.trackError({
          type: 'warning',
          message: `confirmAge crash, error: ${error}`,
        });
      } finally {
        dispatch(
          updateFeatureFlag({
            flag: 'isNeedAgeVerificationEnabled',
            value: false,
          })
        );
        handleClose();
      }
    }
  };

  useEffect(() => {
    if (!isNeedAgeVerificationEnabled || !MirrorService.sumsubToken) return;

    getAccessToken();
  }, [isNeedAgeVerificationEnabled]);

  if (!isNeedAgeVerificationEnabled || !MirrorService.sumsubToken) return null;

  return (
    <Popup>
      {accessToken && (
        <SumsubWebSdk
          accessToken={accessToken}
          expirationHandler={handleExpiration}
          onError={handleError}
          onMessage={handleResult}
          config={config}
          options={options}
        />
      )}
    </Popup>
  );
};
